import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';

import { TabPanel, TabList, Tab, Tabs } from 'ui-components/components/tabs';
import { Text, TextType } from 'ui-components/components/text';

import { useEnhancedReturnsCountContext } from '../../enhanced-returns-count-context';
import { SSRAwaitingShipmentGrid } from './grids/awaiting-shipment-container';
import { SSRProcessingGrid } from './grids/processing-container';
import { SSRFlaggedGrid } from './grids/flagged-container';
import { SSRCompletedGrid } from './grids/completed-container';
import { viewMessages } from '../messages';
import styles from '../styles.module.less';
import { RETURNS_GRID_SELECT_STATUS_TAB } from '../../../../constants/analytics-events';
import { Segment } from '../../../../services/analytics-services/segment';
import {
  PATH_ENHANCED_RETURNS,
  STATUS_COMPLETED,
  STATUS_AWAITING_SHIPMENT,
  STATUS_PROCESSING,
  STATUS_FLAGGED,
  useEnhancedReturnRouteMatch,
  resetPageQueryParam,
} from '../../../../store/returns/returns.helpers';

export const SSRStatusTabs = () => {
  const history = useHistory();
  const { storeGuid, statusViewId } = useEnhancedReturnRouteMatch();
  const { countsPerStoreMap } = useEnhancedReturnsCountContext();
  const searchResultsForStore = countsPerStoreMap[storeGuid];

  const statusViewCountMap = useMemo(() => searchResultsForStore?.statusViewCountMap || {}, [searchResultsForStore]);

  const tabIndexToStatusViewIdMap = useMemo<Record<number, string>>(
    () => ({
      0: STATUS_AWAITING_SHIPMENT,
      1: STATUS_PROCESSING,
      2: STATUS_FLAGGED,
      3: STATUS_COMPLETED,
    }),
    [],
  );

  const getStatusFromTabIndex = useCallback((index: number) => tabIndexToStatusViewIdMap[index], [tabIndexToStatusViewIdMap]);
  const getTabIndexFromStatus = useCallback(
    (status: string) => {
      const stringyIndex = Object.keys(tabIndexToStatusViewIdMap).find((key) => tabIndexToStatusViewIdMap[Number(key)] === status);
      return stringyIndex ? parseInt(stringyIndex, 10) : 0;
    },
    [tabIndexToStatusViewIdMap],
  );

  const selectedTabIndex = getTabIndexFromStatus(statusViewId ?? '');

  const changeView = useCallback(
    (tabIndex: number) => {
      const viewId = getStatusFromTabIndex(tabIndex);

      Segment.trackEvent(RETURNS_GRID_SELECT_STATUS_TAB, {
        type: RETURNS_GRID_SELECT_STATUS_TAB,
        viewId,
      });

      const newParams = resetPageQueryParam(history.location.search);
      history.push({
        pathname: `${PATH_ENHANCED_RETURNS}/${storeGuid}/${viewId}`,
        search: newParams,
      });
    },
    [history, storeGuid, getStatusFromTabIndex],
  );

  const tabProps = useMemo(
    () => [
      {
        id: STATUS_AWAITING_SHIPMENT,
        name: <FormattedMessage {...viewMessages.awaitingShipment} />,
        count: statusViewCountMap[STATUS_AWAITING_SHIPMENT] ?? 0,
      },
      {
        id: STATUS_PROCESSING,
        name: <FormattedMessage {...viewMessages.processing} />,
        count: statusViewCountMap[STATUS_PROCESSING] ?? 0,
      },
      {
        id: STATUS_FLAGGED,
        name: <FormattedMessage {...viewMessages.flagged} />,
        count: statusViewCountMap[STATUS_FLAGGED] ?? 0,
      },
      {
        id: STATUS_COMPLETED,
        name: <FormattedMessage {...viewMessages.completed} />,
        count: statusViewCountMap[STATUS_COMPLETED] ?? 0,
        shouldHideCount: true,
      },
    ],
    [statusViewCountMap],
  );

  return (
    <Tabs className={styles.tabs} onSelectedTabIndexChange={changeView} selectedTabIndex={selectedTabIndex}>
      <TabList className={styles['tab-list']}>
        {tabProps.map((tab, index) => (
          <Tab key={tab.id}>
            {tab.name}
            {!tab.shouldHideCount && tab.count > 0 && (
              <Text
                className={classnames(styles['tab-count'], {
                  [styles.selected]: selectedTabIndex === index,
                })}
                textType={TextType.Caption}
                value={tab.count}
              />
            )}
          </Tab>
        ))}
      </TabList>
      <TabPanel selectedClassName={styles.tabs}>
        <SSRAwaitingShipmentGrid />
      </TabPanel>
      <TabPanel selectedClassName={styles.tabs}>
        <SSRProcessingGrid />
      </TabPanel>
      <TabPanel selectedClassName={styles.tabs}>
        <SSRFlaggedGrid />
      </TabPanel>
      <TabPanel selectedClassName={styles.tabs}>
        <SSRCompletedGrid />
      </TabPanel>
    </Tabs>
  );
};
